import { useState } from 'react';
import { Drawer } from 'antd';
import { Link } from 'react-router-dom';
import { Button } from 'components/ui/button';
import type { Incident, UpdateIncident, WorkStatus } from './contracts';
import { useAlertActions, useIncidentHistory, unifiedAlertsService } from './useUnifiedAlerts';
import type { UnifiedAlertsService } from './useUnifiedAlerts';
import { alertDate, ErrorNotice, Field, incidentStatusLabels, LoadingNotice } from './shared';
import { isVersionConflict } from './transport';

const conditionLabels = {
  firing: 'Betingelsen er stadig opfyldt',
  recovered: 'Normal drift registreret',
  unknown: 'Tilstanden kan ikke vurderes',
};
const timelineLabels: Record<string, string> = {
  opened: 'Hændelse åbnet',
  observation: 'Ny observation',
  recovered: 'Normal drift registreret',
  unknown: 'Tilstanden er ukendt',
  acknowledged: 'Markeret som set',
  in_progress: 'Behandling startet',
  resolved: 'Hændelse afsluttet',
  reminder: 'Påmindelse',
};
const reasonLabels = {
  recovered: 'Normal drift bekræftet',
  fixed: 'Fejlen er rettet',
  false_alarm: 'Falsk alarm',
  not_in_use: 'Enheden er taget ud af brug',
  other: 'Anden årsag',
};

export function IncidentActions({
  incident,
  onSave,
  onReload,
}: {
  incident: Incident;
  onSave: (command: UpdateIncident) => Promise<{ incident: Incident }>;
  onReload: () => Promise<Incident | undefined>;
}) {
  const [base, setBase] = useState(incident);
  const [status, setStatus] = useState<WorkStatus>(incident.status);
  const [reason, setReason] = useState<UpdateIncident['resolutionReason']>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  const stale = incident.version > base.version || conflict;

  async function save() {
    if (busy || stale) return;
    if (status === 'resolved' && (!reason || !note.trim())) {
      setError('Vælg en årsag og skriv en note, før hændelsen afsluttes.');
      return;
    }
    if (status === base.status && !note.trim()) {
      setError('Skriv en note eller vælg en ny status.');
      return;
    }
    setBusy(true);
    setError(null);
    setMessage('');
    try {
      const result = await onSave({
        incidentId: base.id,
        expectedVersion: base.version,
        status,
        resolutionReason: status === 'resolved' ? reason : null,
        note: note.trim(),
      });
      setBase(result.incident);
      setStatus(result.incident.status);
      setNote('');
      setReason(null);
      setMessage('Ændringen er gemt i tidslinjen.');
    } catch (cause) {
      setError(cause);
      setConflict(isVersionConflict(cause));
    } finally {
      setBusy(false);
    }
  }
  async function reload() {
    setBusy(true);
    try {
      const latest = await onReload();
      if (!latest) throw new Error('Status kunne ikke genindlæses. Noten er bevaret.');
      setBase(latest);
      setStatus(latest.status);
      setReason(null);
      setConflict(false);
      setError(null);
      setMessage('Seneste status er hentet. Din ugemte note er bevaret.');
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }
  if (incident.status === 'resolved' || base.status === 'resolved') {
    const resolved = incident.status === 'resolved' ? incident : base;
    return (
      <div className="ua-notice ua-success">
        <div>
          <strong>Hændelsen er afsluttet</strong>
          <p>{resolved.resolutionReason ? reasonLabels[resolved.resolutionReason] : ''}</p>
          <p>{resolved.resolutionNote}</p>
          <small>Ny gentagelse opretter en ny hændelse; denne historik bevares.</small>
        </div>
      </div>
    );
  }
  return (
    <form
      className="ua-stack"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <h3>Opfølgning</h3>
      <fieldset disabled={busy} className="ua-stack">
        <Field label="Status">
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as WorkStatus);
              setError(null);
            }}
          >
            {Object.entries(incidentStatusLabels).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        {status === 'resolved' && (
          <Field label="Årsag til afslutning">
            <select
              value={reason || ''}
              onChange={(event) =>
                setReason(event.target.value as UpdateIncident['resolutionReason'])
              }
            >
              <option value="">Vælg en årsag</option>
              {Object.entries(reasonLabels).map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field
          label={status === 'resolved' ? 'Afsluttende note' : 'Note'}
          hint="Noten bliver en del af hændelsens tidslinje."
        >
          <textarea
            rows={3}
            maxLength={2000}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
      </fieldset>
      {stale && (
        <div className="ua-notice ua-warning" role="alert">
          Hændelsen er ændret siden åbningen. Hent seneste status, før du gemmer. Din note bevares.
          <Button type="button" variant="outline" disabled={busy} onClick={() => void reload()}>
            Hent seneste status
          </Button>
        </div>
      )}
      <ErrorNotice error={error} />
      {message && (
        <p className="ua-notice ua-success" role="status">
          {message}
        </p>
      )}
      <div>
        <Button type="submit" disabled={busy || stale}>
          {busy ? 'Gemmer…' : status === 'resolved' ? 'Afslut hændelse' : 'Gem opfølgning'}
        </Button>
      </div>
    </form>
  );
}

export default function IncidentDrawer({
  incidentId,
  onClose,
  service = unifiedAlertsService,
}: {
  incidentId: string;
  onClose: () => void;
  service?: UnifiedAlertsService;
}) {
  const { query, incident, timeline } = useIncidentHistory(incidentId, service);
  const { updateIncident } = useAlertActions(service);
  return (
    <Drawer
      open
      width={700}
      title={incident?.ruleTitle || 'Hændelse'}
      onClose={onClose}
      destroyOnClose
    >
      <div className="unified-alerts ua-stack">
        {query.isPending && <LoadingNotice>Henter hændelse og tidslinje…</LoadingNotice>}
        <ErrorNotice
          error={query.isError ? query.error : null}
          onRetry={() => void query.refetch()}
        />
        {incident && (
          <>
            <div className="ua-header">
              <div>
                <h2>{incident.sensorTitle}</h2>
                <p className="ua-muted">{incident.orgTitle}</p>
              </div>
              <span className={`ua-pill ${incident.status}`}>
                {incidentStatusLabels[incident.status]}
              </span>
            </div>
            <div className="ua-inline">
              <span className={`ua-pill ${incident.severity}`}>
                {incident.severity === 'critical' ? 'Kritisk' : 'Advarsel'}
              </span>
              <span className="ua-pill">{incident.mode === 'live' ? 'LIVE' : 'Test'}</span>
              <span>{conditionLabels[incident.conditionState]}</span>
            </div>
            <dl className="ua-details">
              <div>
                <dt>Første registrering</dt>
                <dd>{alertDate(incident.openedAt)}</dd>
              </div>
              <div>
                <dt>Seneste observation</dt>
                <dd>{alertDate(incident.lastObservedAt)}</dd>
              </div>
              <div>
                <dt>Samlede observationer</dt>
                <dd>{incident.occurrenceCount}</dd>
              </div>
              <div>
                <dt>Regelversion</dt>
                <dd>{incident.ruleVersion}</dd>
              </div>
            </dl>
            {incident.detailPath.startsWith('/') && !incident.detailPath.startsWith('//') && (
              <Link className="ua-link" to={incident.detailPath}>
                Åbn enheden
              </Link>
            )}
            {query.isError && (
              <p className="ua-notice ua-warning">
                De viste oplysninger er fra seneste vellykkede indlæsning. Opdatér før en ny
                vurdering.
              </p>
            )}
            <IncidentActions
              key={incident.id}
              incident={incident}
              onSave={updateIncident}
              onReload={async () => (await query.refetch()).data?.pages[0]?.incident}
            />
            <hr className="ua-divider" />
            <div className="ua-header">
              <h3>Tidslinje</h3>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={query.isFetching}
                onClick={() => void query.refetch()}
              >
                {query.isFetching && !query.isFetchingNextPage ? 'Opdaterer…' : 'Opdatér'}
              </Button>
            </div>
            <ol className="ua-timeline">
              {timeline.map((event) => (
                <li key={event.id}>
                  <strong>{timelineLabels[event.type] || event.type}</strong>
                  <div>
                    <time>{alertDate(event.at)}</time>
                    {event.actor && <span className="ua-muted"> · {event.actor}</span>}
                  </div>
                  {event.note && <p>{event.note}</p>}
                  {event.value !== null && (
                    <p className="ua-muted">Registreret værdi: {String(event.value)}</p>
                  )}
                  {event.observedAt !== event.at && (
                    <small className="ua-muted">
                      Kildens tidspunkt: {alertDate(event.observedAt)}
                    </small>
                  )}
                </li>
              ))}
            </ol>
            {!timeline.length && !query.isError && (
              <p className="ua-muted">Ingen tidslinjeposter er indlæst.</p>
            )}
            {query.hasNextPage && (
              <Button
                type="button"
                variant="outline"
                disabled={query.isFetching}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? 'Henter ældre poster…' : 'Hent ældre poster'}
              </Button>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
